import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button } from '../components/Button'
import { api } from '../api/client'
import { describeError } from '../lib/describeError'
import { reviseFigureNote } from '../editorial/piece/pieceHelpers'
import { FORMAT_OPTIONS, reviseFormatHint, type Brief } from '../lib/brief'

const LABEL: Record<string, string> = { short: 'Short', long: 'Long', deep: 'Deep research', legacy: 'Before formats' }

/** "Revise with feedback": the editor's note, and optionally a different format. The rewrite uses the
 *  same stored sources; the status line shows it while it runs. */
export function ReviseForm({ draftId, currentFormat, blockedHint, bodyHtml, onClose, onStarted }: {
  draftId: number; currentFormat: Brief['format'] | undefined
  /** Set while Revise is unavailable (unsaved edits): the submit is disabled and this shows instead. */
  blockedHint?: string | null
  /** The piece's body: photos placed in it are dropped by a rewrite, and the form says so. */
  bodyHtml?: string | null
  onClose: () => void; onStarted: () => void
}) {
  const [note, setNote] = useState('')
  const [format, setFormat] = useState('')
  const trimmed = note.trim()
  const figureNote = reviseFigureNote(bodyHtml)
  const formatHint = reviseFormatHint(currentFormat, format)
  const revise = useMutation({
    mutationFn: () => api.post<{ started: boolean }>(`/api/lab/section-drafts/${draftId}/revise`, { note: trimmed, format: format || null }),
    onSuccess: onStarted,
  })
  return (
    <form className="mt-3 border border-ink-600 rounded p-3 space-y-2"
          onSubmit={(e) => { e.preventDefault(); if (trimmed && !blockedHint && !revise.isPending) revise.mutate() }}>
      <label className="block">
        <span className="label">What should change? (required)</span>
        <textarea rows={4} required maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Lead with the restoration story and cut the second history section."
                  className="mt-1 w-full bg-ink-900 border border-ink-600 text-paper text-[14px] p-2 font-sans" />
        <span className="label">{note.length}/1000</span>
      </label>
      <label className="block">
        <span className="label">Format</span>
        <select value={format} onChange={(e) => setFormat(e.target.value)}
                className="mt-1 block bg-ink-900 border border-ink-600 text-paper text-[13px] p-1.5">
          <option value="">Keep {currentFormat ? (LABEL[currentFormat] ?? currentFormat) : 'current'}</option>
          {FORMAT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </label>
      {formatHint && <p className="text-amber-glow text-[13px]">{formatHint}</p>}
      <p className="label normal-case tracking-normal">Rewrites from the same sources; nothing new is researched.</p>
      {figureNote && <p className="text-amber-glow text-[13px]">{figureNote}</p>}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={!trimmed || !!blockedHint || revise.isPending}>{revise.isPending ? 'Starting…' : 'Revise'}</Button>
        <Button type="button" variant="ghost" onClick={onClose} disabled={revise.isPending}>Cancel</Button>
        {blockedHint && <span className="label">{blockedHint}</span>}
      </div>
      {revise.isError && <p className="text-red-300 text-[13px]">{describeError(revise.error)}</p>}
    </form>
  )
}
